import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChartBar } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function DistributionBar({ items, colorMap }) {
  const max = Math.max(...Object.values(items), 1);
  return (
    <div className="space-y-1.5">
      {Object.entries(items).map(([key, count]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-[11px] text-[#666] w-20 text-right capitalize truncate">{key}</span>
          <div className="flex-1 h-4 bg-white/[0.02] rounded overflow-hidden">
            <div className="h-full rounded transition-all duration-500" style={{
              width: `${(count / max) * 100}%`, backgroundColor: colorMap?.[key] || '#555',
            }} />
          </div>
          <span className="text-[11px] text-white font-medium w-8 tabular-nums">{count}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin-console/analytics`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="grid grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-48 bg-white/[0.03] rounded-lg animate-pulse" />)}</div>;
  if (!data) return <p className="text-[#444]">Failed to load analytics.</p>;

  const roleColors = { admin: '#EF4444', moderator: '#F59E0B', user: '#00F0FF' };
  const sessionColors = { open: '#10B981', in_progress: '#F59E0B', completed: '#00F0FF', cancelled: '#EF4444', full: '#9D4CDD' };
  const tournColors = { registration: '#00F0FF', in_progress: '#F59E0B', completed: '#10B981', cancelled: '#EF4444' };
  const tierColors = { bronze: '#CD7F32', silver: '#C0C0C0', gold: '#FFD700', platinum: '#E5E4E2' };

  return (
    <div data-testid="admin-analytics-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Platform Analytics</h1>
        <p className="text-[13px] text-[#444] mt-0.5">Aggregate platform metrics</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4">
          <h3 className="text-[13px] font-semibold text-white mb-3">Users by Role</h3>
          <DistributionBar items={data.users_by_role} colorMap={roleColors} />
        </div>
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4">
          <h3 className="text-[13px] font-semibold text-white mb-3">Sessions by Status</h3>
          <DistributionBar items={data.sessions_by_status} colorMap={sessionColors} />
        </div>
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4">
          <h3 className="text-[13px] font-semibold text-white mb-3">Tournaments by Status</h3>
          <DistributionBar items={data.tournaments_by_status} colorMap={tournColors} />
        </div>
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4">
          <h3 className="text-[13px] font-semibold text-white mb-3">Shop Tiers</h3>
          {data.shops_by_tier && Object.keys(data.shops_by_tier).length > 0 ? (
            <DistributionBar items={data.shops_by_tier} colorMap={tierColors} />
          ) : <p className="text-[12px] text-[#333]">No data</p>}
        </div>
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4 lg:col-span-2">
          <h3 className="text-[13px] font-semibold text-white mb-3">Top Games (by Sessions)</h3>
          {data.top_games?.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              {data.top_games.map((g, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/[0.02] border border-white/[0.04]">
                  <span className="text-[11px] text-[#333] font-bold tabular-nums">{i + 1}</span>
                  <div>
                    <p className="text-[12px] text-white font-medium">{g.game}</p>
                    <p className="text-[10px] text-[#00F0FF] tabular-nums">{g.count} sessions</p>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-[12px] text-[#333]">No session data</p>}
        </div>
      </div>
    </div>
  );
}
